import React, { useState, useEffect, useRef } from 'react';
import API, { BACKEND_URL } from '../api';
import { Plus, Search, Phone, Mail, X, ArrowLeft, Upload, Trash2, Download, File, Image, BarChart3, FolderOpen, HardHat, FileText, ChevronRight, TrendingUp, Users } from 'lucide-react';
import RowActions from '../components/RowActions';
import { useConfirm } from '../components/ConfirmDialog';

export default function ClientiPage({ onNavigate }) {
  const confirm = useConfirm();
  const [clienti, setClienti] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', codice_fiscale: '', partita_iva: '', telefono: '', email: '', indirizzo: '', numero_civico: '', cap: '', comune: '', provincia: '', nazione: 'IT', codice_destinatario: '', pec: '', is_pa: false, note: '' });
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState('overview');
  const [uploading, setUploading] = useState(false);
  const [capComuni, setCapComuni] = useState([]);
  const [capMsg, setCapMsg] = useState('');
  const fileRef = useRef(null);

  const handleCap = async (raw) => {
    const cap = raw.replace(/\D/g, '').slice(0, 5);
    setForm(f => ({ ...f, cap }));
    setCapComuni([]); setCapMsg('');
    if (cap.length !== 5) return;
    try {
      const { data } = await API.get(`/api/geo/cap/${cap}`);
      const list = data.comuni || [];
      if (list.length === 1) {
        setForm(f => ({ ...f, cap, comune: list[0].comune, provincia: list[0].sigla }));
      } else if (list.length > 1) {
        setCapComuni(list);
        setCapMsg(`${list.length} comuni per questo CAP: seleziona quello corretto`);
      } else {
        setCapMsg('CAP non trovato: inserisci Comune e Provincia manualmente');
      }
    } catch {
      setCapMsg('Lookup CAP non disponibile: inserisci Comune e Provincia manualmente');
    }
  };

  const pickComune = (c) => {
    setForm(f => ({ ...f, comune: c.comune, provincia: c.sigla }));
    setCapComuni([]); setCapMsg('');
  };

  const load = () => API.get('/api/clienti').then(r => setClienti(r.data));
  useEffect(() => { load(); }, []);

  const filtered = clienti.filter(c => {
    const s = search.toLowerCase();
    return !s || (c.nome||'').toLowerCase().includes(s) || (c.cognome||'').toLowerCase().includes(s) || (c.ragione_sociale||'').toLowerCase().includes(s) || (c.email||'').toLowerCase().includes(s);
  });

  const save = async () => {
    if (editing) { await API.put(`/api/clienti/${editing}`, form); }
    else { await API.post('/api/clienti', form); }
    setShowForm(false); setEditing(null);
    setForm({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', codice_fiscale: '', partita_iva: '', telefono: '', email: '', indirizzo: '', numero_civico: '', cap: '', comune: '', provincia: '', nazione: 'IT', codice_destinatario: '', pec: '', is_pa: false, note: '' });
    load();
  };

  const edit = (c) => { setForm(c); setEditing(c.id); setShowForm(true); };
  const remove = async (id) => { if (await confirm({ title: 'Eliminare cliente?', message: 'Verranno rimosse anche le sue associazioni a pratiche e documenti.', confirmLabel: 'Elimina', variant: 'danger' })) { await API.delete(`/api/clienti/${id}`); load(); setSelected(null); } };

  const openCliente = async (c) => {
    setSelected(c); setTab('overview');
    const [statsRes, docsRes] = await Promise.all([
      API.get(`/api/clienti/${c.id}/stats`),
      API.get(`/api/clienti/${c.id}/documenti`),
    ]);
    setStats(statsRes.data); setDocs(docsRes.data);
  };

  const uploadDoc = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file); fd.append('categoria', file.type.startsWith('image/') ? 'foto' : 'documento');
    try {
      await API.post(`/api/clienti/${selected.id}/documenti`, fd);
      const { data } = await API.get(`/api/clienti/${selected.id}/documenti`); setDocs(data);
    } catch {} setUploading(false);
  };

  const deleteDoc = async (did) => {
    if (await confirm({ title: 'Eliminare documento?', message: 'Il file verrà rimosso dal cliente.', confirmLabel: 'Elimina', variant: 'danger' })) {
      await API.delete(`/api/clienti/${selected.id}/documenti/${did}`);
      const { data } = await API.get(`/api/clienti/${selected.id}/documenti`); setDocs(data);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const getName = (c) => {
    const t = c.tipo_cliente;
    if (t === 'azienda' || t === 'condominio' || t === 'pa') {
      return c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim();
    }
    return `${c.nome || ''} ${c.cognome || ''}`.trim();
  };
  const TIPI_CLIENTE = {
    privato: { label: 'Privato', color: 'bg-slate-100 text-slate-700' },
    azienda: { label: 'Azienda', color: 'bg-blue-100 text-blue-700' },
    condominio: { label: 'Condominio', color: 'bg-purple-100 text-purple-700' },
    pa: { label: 'Pubblica Amministrazione', color: 'bg-emerald-100 text-emerald-700' },
  };
  const tipoLabel = (t) => (TIPI_CLIENTE[t] || TIPI_CLIENTE.privato).label;
  const tipoColor = (t) => (TIPI_CLIENTE[t] || TIPI_CLIENTE.privato).color;
  const statiPratica = { nuova: 'Nuova', in_valutazione: 'In Valutazione', approvata: 'Approvata', in_cantiere: 'In Cantiere', completata: 'Completata', rifiutata: 'Rifiutata' };
  const statiColors = { nuova: 'bg-blue-100 text-blue-700', approvata: 'bg-green-100 text-green-700', in_cantiere: 'bg-emerald-100 text-emerald-800', completata: 'bg-slate-200 text-slate-600', rifiutata: 'bg-red-100 text-red-700' };

  // ── Detail View ───────────────────────────────────────────────────
  if (selected && stats) {
    return (
      <div className="animate-fade-in" data-testid="cliente-detail">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button onClick={() => { setSelected(null); setStats(null); }} className="p-2 hover:bg-slate-100 rounded-md flex-shrink-0" data-testid="back-clienti">
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight truncate">{getName(selected)}</h1>
              <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded font-bold ${tipoColor(selected.tipo_cliente)}`}>{tipoLabel(selected.tipo_cliente)}</span>
                {selected.telefono && <span className="flex items-center gap-1"><Phone size={11}/>{selected.telefono}</span>}
                {selected.email && <span className="flex items-center gap-1"><Mail size={11}/>{selected.email}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0 items-center">
            <button onClick={() => edit(selected)} className="px-3 py-1.5 border border-slate-200 text-sm font-bold rounded-md hover:bg-slate-50">Modifica</button>
            <RowActions entity="clienti" id={selected.id} label="il cliente" onChange={() => { setSelected(null); setStats(null); load(); }} />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatCard label="Fatturato" value={fmt(stats.fatturato_totale)} sub="IVA inclusa" color="text-primary" icon={TrendingUp} highlight />
          <StatCard label="Pratiche Attive" value={stats.pratiche_attive} sub={`${stats.pratiche_chiuse} chiuse`} color="text-accent-info" icon={FolderOpen} />
          <StatCard label="Preventivi" value={stats.preventivi_approvati} sub={`${stats.preventivi_totali} totali`} color="text-purple-600" icon={FileText} />
          <StatCard label="Cantieri" value={stats.cantieri_attivi} sub={`${stats.cantieri_completati} completati`} color="text-accent-success" icon={HardHat} />
        </div>

        {/* Revenue breakdown */}
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-primary" /> Dettaglio Fatturato</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">Imponibile Approvato</p>
              <p className="text-lg font-black font-mono">{fmt(stats.fatturato_imponibile)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">Totale con IVA</p>
              <p className="text-lg font-black font-mono text-primary">{fmt(stats.fatturato_totale)}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase">In Bozza</p>
              <p className="text-lg font-black font-mono text-slate-400">{fmt(stats.fatturato_bozze)}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-white border border-slate-200 rounded-md p-1 overflow-x-auto">
          {[{id:'overview',label:'Pratiche'},{id:'documenti',label:`Documenti (${docs.length})`}].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded text-sm font-bold transition-all whitespace-nowrap ${tab === t.id ? 'bg-surface-dark text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Pratiche Tab */}
        {tab === 'overview' && (
          <div className="space-y-2" data-testid="cliente-pratiche">
            {stats.pratiche?.map(p => (
              <button key={p.id} onClick={() => onNavigate && onNavigate('pratiche', { open_pratica: p.id })}
                className="w-full bg-white border border-slate-200 rounded-md p-3 text-left hover:shadow-sm transition-all group flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{p.codice_pratica}</span>
                    <h3 className="font-bold text-sm">{p.titolo}</h3>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${statiColors[p.stato] || 'bg-slate-100'}`}>{statiPratica[p.stato] || p.stato}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{p.descrizione_richiesta?.substring(0, 80)}</p>
                </div>
                <ChevronRight size={16} className="text-slate-300 group-hover:text-primary flex-shrink-0" />
              </button>
            ))}
            {(!stats.pratiche || stats.pratiche.length === 0) && <p className="text-center py-8 text-slate-400 text-sm">Nessuna pratica per questo cliente</p>}
          </div>
        )}

        {/* Documenti Tab */}
        {tab === 'documenti' && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-slate-500">Documenti e fotografie del cliente</p>
              <div>
                <input type="file" ref={fileRef} className="hidden" onChange={uploadDoc} />
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-cliente"
                  className="px-3 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all flex items-center gap-1.5 text-sm disabled:opacity-50">
                  <Upload size={14} /> {uploading ? 'Caricamento...' : 'Carica'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cliente-docs">
              {docs.map(d => {
                const isImage = ['jpg','jpeg','png','webp','gif'].includes(d.tipo_file);
                return (
                  <div key={d.id} className="bg-white border border-slate-200 rounded-md overflow-hidden group">
                    {isImage && d.file_url && (
                      <div className="h-32 bg-slate-100 overflow-hidden">
                        <img src={`${BACKEND_URL}${d.file_url}`} alt={d.filename} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-3 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {isImage ? <Image size={14} className="text-blue-500 flex-shrink-0" /> : <File size={14} className="text-slate-400 flex-shrink-0" />}
                          <p className="font-semibold text-xs truncate">{d.filename}</p>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">{d.dimensione ? `${(d.dimensione/1024).toFixed(1)} KB` : ''} - {d.created_at ? new Date(d.created_at).toLocaleDateString('it-IT') : ''}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <a href={`${BACKEND_URL}${d.file_url}`} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-slate-100 rounded"><Download size={14} className="text-slate-400" /></a>
                        <button onClick={() => deleteDoc(d.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {docs.length === 0 && <p className="col-span-full text-center py-8 text-slate-400 text-sm">Nessun documento. Carica il primo!</p>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── List View ─────────────────────────────────────────────────────
  return (
    <div className="animate-fade-in" data-testid="clienti-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3"><Users size={28} className="text-primary" /> Clienti</h1>
        <button data-testid="add-cliente-btn" onClick={() => { setShowForm(true); setEditing(null); setForm({ tipo_cliente: 'privato', nome: '', cognome: '', ragione_sociale: '', codice_fiscale: '', partita_iva: '', telefono: '', email: '', indirizzo: '', numero_civico: '', cap: '', comune: '', provincia: '', nazione: 'IT', codice_destinatario: '', pec: '', is_pa: false, note: '' }); }}
          className="px-3 py-2 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95 flex items-center gap-1.5 text-sm">
          <Plus size={16} /> Nuovo Cliente
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="search-clienti" placeholder="Cerca clienti..." className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className="space-y-2">
        {filtered.map(c => (
          <button key={c.id} onClick={() => openCliente(c)} data-testid={`cliente-${c.id}`}
            className="w-full bg-white border border-slate-200 rounded-md p-4 text-left hover:shadow-sm transition-all group flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold">{getName(c)}</p>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${tipoColor(c.tipo_cliente)}`}>
                  {tipoLabel(c.tipo_cliente)}
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-400 mt-1 flex-wrap">
                {(c.indirizzo || c.numero_civico) && <span>{[c.indirizzo, c.numero_civico].filter(Boolean).join(' ')}</span>}
                {c.telefono && <span className="flex items-center gap-1"><Phone size={10}/>{c.telefono}</span>}
                {c.email && <span className="flex items-center gap-1"><Mail size={10}/>{c.email}</span>}
              </div>
            </div>
            <ChevronRight size={16} className="text-slate-300 group-hover:text-primary flex-shrink-0" />
          </button>
        ))}
        {filtered.length === 0 && <p className="text-center py-8 text-slate-400">Nessun cliente trovato</p>}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="cliente-modal">
          <div className="bg-white rounded-t-2xl sm:rounded-md w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[90vh] flex flex-col animate-fade-in shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-200 bg-white sticky top-0 z-10">
              <h3 className="text-xl font-bold">{editing ? 'Modifica Cliente' : 'Nuovo Cliente'}</h3>
              <button onClick={() => {
                const hasData = form.nome || form.cognome || form.ragione_sociale || form.email || form.telefono || form.partita_iva || form.codice_fiscale;
                if (hasData && !editing && !window.confirm('Hai dati non salvati. Vuoi davvero chiudere?')) return;
                setShowForm(false);
              }} aria-label="Chiudi"><X size={20} className="text-slate-400 hover:text-slate-800" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-6">
            <div className="space-y-3">
              <select data-testid="cliente-tipo" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.tipo_cliente} onChange={e => {
                const newTipo = e.target.value;
                setForm({...form, tipo_cliente: newTipo, is_pa: newTipo === 'pa'});
              }}>
                <option value="privato">Privato</option>
                <option value="azienda">Azienda</option>
                <option value="condominio">Condominio</option>
                <option value="pa">Pubblica Amministrazione</option>
              </select>
              {(form.tipo_cliente === 'azienda' || form.tipo_cliente === 'condominio' || form.tipo_cliente === 'pa') ? (
                <input data-testid="cliente-ragione" placeholder={form.tipo_cliente === 'condominio' ? 'Denominazione condominio' : form.tipo_cliente === 'pa' ? 'Denominazione Ente Pubblico' : 'Ragione Sociale'} className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.ragione_sociale} onChange={e => setForm({...form, ragione_sociale: e.target.value})} />
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="cliente-nome" placeholder="Nome" className="px-3 py-2.5 border border-slate-200 rounded-md" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} />
                  <input data-testid="cliente-cognome" placeholder="Cognome" className="px-3 py-2.5 border border-slate-200 rounded-md" value={form.cognome} onChange={e => setForm({...form, cognome: e.target.value})} />
                </div>
              )}
              <input placeholder="Codice Fiscale" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.codice_fiscale} onChange={e => setForm({...form, codice_fiscale: e.target.value})} />
              {(form.tipo_cliente === 'azienda' || form.tipo_cliente === 'condominio' || form.tipo_cliente === 'pa') && <input placeholder={form.tipo_cliente === 'pa' ? 'Codice IPA / P.IVA' : 'Partita IVA'} className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.partita_iva} onChange={e => setForm({...form, partita_iva: e.target.value})} />}
              <input data-testid="cliente-telefono" placeholder="Telefono" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
              <input data-testid="cliente-email" placeholder="Email" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
              <input data-testid="cliente-indirizzo" placeholder="Indirizzo" className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.indirizzo} onChange={e => setForm({...form, indirizzo: e.target.value})} />
              <div className="grid grid-cols-2 sm:grid-cols-12 gap-2">
                <input placeholder="Civico" className="sm:col-span-3 px-3 py-2.5 border border-slate-200 rounded-md min-w-0" value={form.numero_civico || ''} onChange={e => setForm({...form, numero_civico: e.target.value})} />
                <input placeholder="CAP" maxLength={5} data-testid="cliente-cap" className="sm:col-span-3 px-3 py-2.5 border border-slate-200 rounded-md min-w-0" value={form.cap || ''} onChange={e => handleCap(e.target.value)} />
                <input placeholder="Comune" data-testid="cliente-comune" className="sm:col-span-4 px-3 py-2.5 border border-slate-200 rounded-md min-w-0" value={form.comune || ''} onChange={e => setForm({...form, comune: e.target.value})} />
                <input placeholder="PR" maxLength={2} data-testid="cliente-provincia" className="sm:col-span-2 px-3 py-2.5 border border-slate-200 rounded-md uppercase min-w-0" value={form.provincia || ''} onChange={e => setForm({...form, provincia: e.target.value.toUpperCase().slice(0,2)})} />
              </div>
              {(capComuni.length > 0 || capMsg) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-2 -mt-1" data-testid="cap-suggestions">
                  {capMsg && <p className="text-xs text-amber-800 mb-1">{capMsg}</p>}
                  {capComuni.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {capComuni.map((c, i) => (
                        <button key={i} type="button" data-testid={`cap-comune-opt-${i}`} onClick={() => pickComune(c)}
                          className="px-2.5 py-1 text-xs rounded-full border border-amber-300 bg-white hover:bg-amber-100 text-amber-900 font-medium transition-colors">
                          {c.comune} ({c.sigla})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2" data-testid="sdi-section">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Fatturazione Elettronica (SDI)</span>
                  <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" checked={!!form.is_pa} onChange={e => setForm({...form, is_pa: e.target.checked})} />
                    Pubblica Amministrazione
                  </label>
                </div>
                <input
                  data-testid="cliente-codice-destinatario"
                  placeholder={form.is_pa ? 'Codice Univoco Ufficio PA (6 caratteri)' : 'Codice Destinatario SDI (7 caratteri, es. "0000000")'}
                  maxLength={form.is_pa ? 6 : 7}
                  className="w-full px-3 py-2.5 border border-slate-200 rounded-md uppercase font-mono text-sm"
                  value={form.codice_destinatario || ''}
                  onChange={e => setForm({...form, codice_destinatario: e.target.value.toUpperCase().replace(/\s/g, '').slice(0, form.is_pa ? 6 : 7)})}
                />
                {!form.is_pa && (
                  <input
                    data-testid="cliente-pec"
                    type="email"
                    placeholder='PEC destinatario (se codice "0000000")'
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm"
                    value={form.pec || ''}
                    onChange={e => setForm({...form, pec: e.target.value})}
                  />
                )}
                <p className="text-[10px] text-slate-500">
                  {form.is_pa
                    ? 'IPA: codice ufficio di 6 caratteri'
                    : 'B2B: codice di 7 caratteri (o "0000000" + PEC)'}
                </p>
              </div>
              <textarea placeholder="Note" rows={2} className="w-full px-3 py-2.5 border border-slate-200 rounded-md" value={form.note} onChange={e => setForm({...form, note: e.target.value})} />
            </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-slate-200 bg-white sticky bottom-0">
              <button data-testid="save-cliente-btn" onClick={save} className="w-full py-2.5 bg-primary text-white font-bold rounded-md hover:bg-primary-hover transition-all active:scale-95">
                {editing ? 'Salva Modifiche' : 'Crea Cliente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color, icon: Icon, highlight }) {
  return (
    <div className={`bg-white border rounded-md p-3 ${highlight ? 'border-primary/30 bg-primary/30' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} className={color} />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-xl font-black ${highlight ? 'text-primary' : ''}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}
